import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import clsx from 'clsx';
import BobLoadingMask from '../../../../components/BobLoadingMask';

import { Grid, GridNoRecords, GridColumn as Column, GridToolbar } from '@progress/kendo-react-grid';
import { process as kendoProcess } from '@progress/kendo-data-query';
import {Operators, TextFilter, NumericFilter} from '@progress/kendo-react-data-tools';
import FilterPanel from '../../../../components/K2AFilterControl/FilterPanel';

import * as BobActions from '../../../../actions/bobActions';
import * as DashboardActions from '../../../../actions/dashboardActions';
import { kendoGridInlineStyling } from '../../../../helpers/kendoGridInlineStyleHelper';
import * as classes from './RuleScoreGrid.module.css';

const initialDataStateFilter = {
    logic:   'and',
    filters: []
};

const initialDataState = {
    sort:   [],
    filter: initialDataStateFilter
};

const ruleGridColumns = [
    { name: 'ruleName', label: 'Rule', filter: TextFilter, operators: Operators.text },
    { name: 'score', label: 'Score', filter: NumericFilter, operators: Operators.numeric }
];

const RuleScoreGrid = ({
    activeNavCompany,
    activeNavSite,
    bobLoading,
    dashboardEndTimeStamp,
    dashboardStartTimeStamp,
    desiredInterval,
    fetchScoringData,
    fetchSensorIds,
    rules,
    ruleScoreKpis,
    selectedEquipment,
    setRules,
    setSelectedEquipmentRule,
    setSelectedRuleSensors,
    setSelectedWeatherSensors,
    selectedEquipmentRule,
    workbookModeEnabled,
    ...props
}) => {
    const [currentEquipment, setCurrentEquipment] = useState();
    const [gridDataState, setGridDataState] = useState(initialDataState);
    const [gridFilterText, setGridFilterText] = useState('');
    const [gridFields] = useState(ruleGridColumns);

    useEffect(() => {
        if (!workbookModeEnabled) {
            setGridDataState({...gridDataState, filter: initialDataStateFilter});
            setGridFilterText('');
        }
    }, [workbookModeEnabled]);

    useEffect(() => {
        if (selectedEquipment !== currentEquipment) {
            setGridDataState(initialDataState);
            setGridFilterText('');
            setCurrentEquipment(selectedEquipment);
        }
    }, [selectedEquipment]);

    useEffect(() => {
        setRules(ruleScoreKpis);
    }, [ruleScoreKpis]);

    const handleClickRule = async dataItem => {
        if (selectedEquipmentRule.ruleId === dataItem.ruleId) {
            return;
        }

        let selectedRuleId = [dataItem.ruleId];
        setSelectedWeatherSensors([]);
        const ids = await fetchSensorIds(activeNavSite.id, selectedEquipment.id, selectedRuleId);
        setSelectedEquipmentRule({ ...dataItem, sensorIds: ids });
        await fetchScoringData(activeNavCompany.id, [], selectedRuleId, dashboardStartTimeStamp.format(), dashboardEndTimeStamp.format(), desiredInterval, { EquipmentId: selectedEquipment.id });
    };

    const ruleNameCell = props => {
        const { dataItem, field } = props;
        const linkPath = `/rules/${dataItem.ruleId}/edit/properties?company=${activeNavCompany.id}`;

        return (
            <td className={selectedEquipmentRule.ruleId === props.dataItem.ruleId ? classes.SelectedNameCell : classes.NameCell} role="none">
                <Link
                    to={linkPath}
                    target="_blank"
                >
                    {dataItem[field]}
                </Link>
            </td>
        );
    };

    const ruleScoreCell = props => {
        const { field } = props;
        let value = props.dataItem[field];
        value = value !== null && value !== '' ? value.toString().includes('.') ? parseFloat(value).toFixed(2) : value : '';

        const colorClass = value === null ? classes.NoColorvalue : value > 74 ? classes.Green : value > 49 ? classes.Yellow : value >= 0 ? classes.Red : classes.NoColor;
        const selected = props.dataItem.ruleId === selectedEquipmentRule.ruleId ? classes.SelectedItem : classes.NameCell;
        return (
            <td className={clsx(props.className, colorClass, selected)} style={props.style}>
                {(value !== null && value !== '' && value !== undefined) ? value : ''}
            </td>
        );
    };

    const onRowClick = e => {
        handleClickRule(e.dataItem);
    };

    const onDataStateChange = e => {
        setGridDataState(e.dataState);
    };

    const clearGridFilter = filter => {
        setGridDataState({...gridDataState, filter: filter ? filter : initialDataStateFilter});
    };

    return (
        <React.Fragment>
            <style>
                { kendoGridInlineStyling }
                {
                    `.k-toolbar {
                        order: 1;
                        border-top: inset 1px rgba(0, 0, 0, 0.12);
                        max-height: 5.8rem;
                    }
                    }
                    `
                }
            </style>
            <div className={classes.Table}>
                <Grid
                    sortable={{
                        mode: 'multiple'
                    }}
                    data={kendoProcess(rules, gridDataState)}
                    {...gridDataState}
                    onDataStateChange={onDataStateChange}
                    className={classes.KendoGrid}
                    filterable={workbookModeEnabled}
                    onRowClick={onRowClick}
                >
                    {
                        workbookModeEnabled ?
                            <GridToolbar>
                                <FilterPanel
                                    title={'Rule Filter Builder'}
                                    filter={gridDataState.filter}
                                    filterText={gridFilterText}
                                    fields={gridFields}
                                    columns={[{name: 'Equipment', field: 'name', filter: 'text'}, {name: 'Score', field: 'score', filter: 'numeric'}]}
                                    setFilter={clearGridFilter}
                                    setFilterText={setGridFilterText}
                                />
                            </GridToolbar>
                            : null
                    }
                    {
                        bobLoading.includes('RuleKpi') ?
                            <GridNoRecords>
                                Loading rules
                            </GridNoRecords>
                            :
                            null
                    }
                    <Column
                        field="ruleName"
                        key="ruleId"
                        title="Rule"
                        filter={'text'}
                        cell={ruleNameCell}
                        width="200px"
                    />
                    <Column
                        field="score"
                        key="score"
                        title="Score"
                        filter={'numeric'}
                        cell={ruleScoreCell}
                        width="160px"
                    />
                </Grid>
                {
                    bobLoading.includes('ScoreData') || bobLoading.includes('SensorIds') || bobLoading.includes('RuleKpi') ?
                        <BobLoadingMask />
                        :
                        null
                }
            </div>
        </React.Fragment>
    );
};

const mapStateToProps = state => {
    return {
        activeNavCompany:        state.bob.activeNavCompany,
        activeNavSite:           state.bob.activeNavSite,
        bobLoading:              state.bob.bobLoading,
        dashboardEndTimeStamp:   state.dashboard.dashboardEndTimeStamp,
        dashboardStartTimeStamp: state.dashboard.dashboardStartTimeStamp,
        desiredInterval:         state.dashboard.desiredInterval,
        entityScoringData:       state.bob.entityScoringData,
        ruleScoreKpis:           state.dashboard.ruleScoreKpis,
        ruleScoringConfig:       state.dashboard.ruleScoringConfig,
        selectedRule:            state.dashboard.selectedRule,
        selectedRuleSensors:     state.dashboard.selectedRuleSensors,
        workbookModeEnabled:     state.dashboard.workbookModeEnabled
    };
};

const mapDispatchToProps = {
    ...BobActions,
    ...DashboardActions
};

export default connect(mapStateToProps, mapDispatchToProps)(RuleScoreGrid);
